import React, { useMemo } from "react";
import {
  Map,
  MapMarker,
  MarkerContent,
  MarkerTooltip,
  MapControls,
} from "@components/ui/map";
import { ActiveVisitor } from "../hooks/useAnalyticsWebSocket";

interface LiveWorldMapProps {
  visitors: ActiveVisitor[];
}

interface GroupedVisitor {
  lat: number;
  lng: number;
  visitors: ActiveVisitor[];
  label: string;
  type: 'anonymous' | 'new_signup' | 'existing_user';
}

const statusColorMap = {
  anonymous: {
    bg: 'bg-amber-500',
    border: 'border-amber-200/50',
    shadow: 'shadow-amber-500/20',
    label: 'Guest'
  },
  new_signup: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-200/50',
    shadow: 'shadow-emerald-500/20',
    label: 'New Signup'
  },
  existing_user: {
    bg: 'bg-blue-500',
    border: 'border-blue-200/50',
    shadow: 'shadow-blue-500/20',
    label: 'Returning User'
  }
};

const getSpectralColor = (count: number) => {
  // Electromagnetic spectrum colors based on intensity
  // count 1 -> Violet, high counts -> Red
  if (count <= 1) return 'rgb(147, 51, 234)'; // Violet
  if (count <= 3) return 'rgb(59, 130, 246)';  // Blue
  if (count <= 10) return 'rgb(16, 185, 129)'; // Green
  if (count <= 30) return 'rgb(234, 179, 8)';  // Yellow
  if (count <= 100) return 'rgb(249, 115, 22)'; // Orange
  return 'rgb(239, 68, 68)';                  // Red
};

const CustomMarkerUI: React.FC<{ 
  count: number; 
  type: 'anonymous' | 'new_signup' | 'existing_user';
}> = ({ count, type }) => {
  const coreColorClass = statusColorMap[type]?.bg || 'bg-amber-500';
  const intensityColor = getSpectralColor(count);
  
  // Number of rings based on density (logarithmic-ish)
  const ringCount = Math.min(Math.floor(Math.log2(count) + 1), 6);
  
  return (
    <div className="relative group/wrapper">
      {/* Dynamic Intensity Rings (Spectrum) - Background density */}
      {[...Array(ringCount)].map((_, i) => (
        <div 
          key={i}
          className="absolute rounded-full border opacity-30 transition-all duration-500 pointer-events-none"
          style={{ 
            width: `${(i + 1) * 4 + 8}px`, 
            height: `${(i + 1) * 4 + 8}px`,
            borderColor: intensityColor,
            borderWidth: '1px',
            transform: 'translate(-50%, -50%)',
            top: '0',
            left: '0'
          }} 
        />
      ))}

      {/* Hover Population Label */}
      <div className="absolute bottom-full mb-3 left-0 -translate-x-1/2 scale-0 group-hover/wrapper:scale-100 transition-transform duration-200 z-[100] origin-bottom pointer-events-none">
         <div className="bg-neutral-900 text-white px-2 py-1 rounded text-[10px] font-black whitespace-nowrap shadow-xl flex items-center gap-1.5 border border-white/20">
            <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: intensityColor }} />
            {count} {statusColorMap[type]?.label || 'Visitor'}{count > 1 ? 's' : ''}
         </div>
         <div className="w-2 h-2 bg-neutral-900 rotate-45 mx-auto -mt-1 border-r border-b border-white/10" />
      </div>

      {/* Core Dot (Static & Minimal) - Represents User Type */}
      <div className={`absolute w-2.5 h-2.5 rounded-full border border-white shadow-sm transition-colors duration-500 ${coreColorClass} -translate-x-1/2 -translate-y-1/2 top-0 left-0`} />
    </div>
  );
};

export const LiveWorldMap: React.FC<LiveWorldMapProps> = ({ visitors }) => {
  const [viewport, setViewport] = React.useState({
    center: [0, 20] as [number, number],
    zoom: 1.5,
    bearing: 0,
    pitch: 0
  });

  const zoom = viewport.zoom;

  const groupedVisitors = useMemo(() => {
    // Grouping by location AND type for smart numbers while preserving color distinction
    const groups: Record<string, GroupedVisitor> = {};

    visitors.forEach((v) => {
      const lat = Number(v.location?.lat);
      const lng = Number(v.location?.lng);
      if (isNaN(lat) || isNaN(lng)) return;

      const type = v.userType || 'anonymous';
      const key = `${lat.toFixed(4)},${lng.toFixed(4)},${type}`;
      
      if (!groups[key]) {
        groups[key] = { 
          lat, 
          lng, 
          visitors: [], 
          label: `${v.location.city}, ${v.location.region || ''}`,
          type
        };
      }
      groups[key].visitors.push(v);
    });

    return Object.values(groups);
  }, [visitors]);

  // Visual scaling factors based on zoom
  // markerScale: 0.6 (world) -> 1.0 (country) -> 1.4 (city)
  const markerScale = Math.min(Math.max(zoom * 0.15 + 0.3, 0.5), 1.5);
  
  // Linear scaling for separation: Zoom goes from roughly 1.5 (world) to 20 (street level).
  // We want max separation (~15px) at max zoom, scaling linearly to 0 at zoom 1.5.
  const normalizedZoom = Math.max(0, (zoom - 1.5) / 18.5);
  const basePixelOffset = normalizedZoom * 15;

  return (
    <div className="w-full h-[500px] rounded-xl overflow-hidden relative group/map">
      <Map
        viewport={viewport}
        onViewportChange={setViewport}
      >
        <MapControls showZoom showFullscreen position="bottom-right" />
        
        {groupedVisitors.map((group) => {
          // Pixel offsets grow linearly as you zoom in to clarify distinct groups.
          // They no longer use coordinate jitter, to ensure purely linear pixel distance.
          const pixelOffsets: Record<string, [number, number]> = {
             anonymous: [0, 0],
             new_signup: [basePixelOffset, -basePixelOffset],
             existing_user: [-basePixelOffset, basePixelOffset]
          };
          const offset = pixelOffsets[group.type] || [0, 0];

          return (
            <MapMarker
              key={`${group.lat}-${group.lng}-${group.type}`}
              longitude={group.lng}
              latitude={group.lat}
              offset={offset}
              anchor="center"
              onMouseEnter={(e) => {
                  const el = (e.currentTarget as HTMLElement);
                  const markerContainer = el.closest('.mapboxgl-marker') as HTMLElement;
                  if (markerContainer) markerContainer.style.zIndex = '9999';
              }}
              onMouseLeave={(e) => {
                  const el = (e.currentTarget as HTMLElement);
                  const markerContainer = el.closest('.mapboxgl-marker') as HTMLElement;
                  if (markerContainer) markerContainer.style.zIndex = '';
              }}
            >
              <MarkerContent>
                 <div style={{ transform: `scale(${markerScale})` }}>
                    <CustomMarkerUI count={group.visitors.length} type={group.type} />
                 </div>
              </MarkerContent>
              <MarkerTooltip>
                <div className="flex items-center gap-2 p-1">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: getSpectralColor(group.visitors.length) }} />
                  <span className="text-[11px] font-black text-neutral-900 tracking-tighter">
                    {group.visitors.length}
                  </span>
                </div>
              </MarkerTooltip>
            </MapMarker>
          );
        })}

        {/* UI Overlays */}
        <div className="absolute top-6 left-6 pointer-events-none z-10">
          <div className="bg-white/80 backdrop-blur-md border border-neutral-100 rounded-2xl px-5 py-3 shadow-sm flex items-center gap-4">
            <div className="relative flex h-3 w-3">
               <div className="absolute inset-0 rounded-full bg-blue-400 opacity-20 animate-ping" />
               <div className="relative rounded-full h-3 w-3 bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.4)]"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-widest leading-none mb-1">
                Live Global Presence
              </span>
              <div className="flex items-baseline gap-2">
                <span className="text-neutral-900 text-2xl font-black tabular-nums">
                  {visitors.length}
                </span>
                <span className="text-blue-600 text-[10px] font-bold uppercase tracking-tight">
                  Active Users
                </span>
              </div>
            </div>
          </div>
        </div>
      </Map>
    </div>
  );
};
